import { Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ButtonSize } from '../../../shared/enums/button.enum';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CartSidebarComponent } from '../cart-sidebar/cart-sidebar.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { select } from '@ngxs/store';
import { take } from 'rxjs';
import { CartState } from '../../../shared/state/cart/cart.state';
import { WooCommerceCartItem } from '../../interfaces/woo-commerce/api/cart/cart.interface';
import {
  RestaurantGeneralState,
  RestaurantState
} from '../../../shared/state/restaurant/restaurant.state';
import { environment } from '../../../../environments/environment';
import {
  StorePickerDialogComponent,
  StorePickerDialogResult
} from '../../../shared/components/dialogs/store-picker-dialog/store-picker-dialog.component';
import { HappyStoreSelectionService } from '../../services/happy/happy-store-selection.service';
import { HappyStoreSwitchService } from '../../services/happy/happy-store-switch.service';
import { HappyStoreDisabledService } from '../../services/happy/happy-store-disabled.service';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  imports: [
    ButtonComponent,
    SidebarComponent,
    CartSidebarComponent,
    ReactiveFormsModule,
    FormsModule
  ],
  styleUrl: './header.component.scss'
})
export class HeaderComponent {
  restaurant: Signal<RestaurantGeneralState> = select(RestaurantState.general);
  cartItems: Signal<WooCommerceCartItem[]> = select(CartState.items);

  isSidebarOpen: WritableSignal<boolean> = signal<boolean>(false);
  isCartOpen: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly ButtonSize = ButtonSize;
  protected readonly restaurantId = (environment as any).id;
  protected readonly headerLinks: { label: string; url: string }[] = (environment as any).headerLinks || [];

  private readonly dialog = inject(Dialog);
  private readonly happyStoreSelection = inject(HappyStoreSelectionService);
  private readonly happyStoreSwitchService = inject(HappyStoreSwitchService);
  private readonly storeValidation = inject(HappyStoreDisabledService);

  protected readonly hasMultipleStores: Signal<boolean> = computed(
    () => this.storeValidation.activeStores().length > 1
  );

  openSidebar() {
    this.isSidebarOpen.set(true);
  }

  openCartSidebar() {
    this.isCartOpen.set(true);
  }

  openStorePicker(): void {
    if (!this.hasMultipleStores()) {
      return;
    }

    const activeStores = this.storeValidation.activeStores();
    const dialogRef = this.dialog.open<StorePickerDialogResult>(
      StorePickerDialogComponent,
      {
        width: '100%',
        maxWidth: '480px',
        data: {
          title: 'Choose a location',
          stores: activeStores.length > 0 ? activeStores : null
        }
      }
    );

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      if (!result) {
        return;
      }

      const envIndex = this.happyStoreSelection.findIndexByDomain(
        result.domain
      );
      if (envIndex >= 0) {
        this.happyStoreSwitchService.switchToStoreIndex(envIndex);
      } else {
        this.happyStoreSelection.selectByDomainWithApiKey(
          result.domain,
          result.apiKey
        );
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }
    });
  }
}
